import React, { useRef, useState } from 'react';
import { ImagePlus, Link2, Loader2, X } from 'lucide-react';
import { BASE, inputCls, Field } from './ui';

/**
 * The optional extras under an email signature: Teams, Telegram and LinkedIn
 * as links, and footer logos, each opening its own link when clicked (the
 * user, 30 Oct 2026). The server reads them the same way
 * (services/agentTeam/text.js, CONTACTS) and refuses one that cannot become a
 * working link, so the preview here matches what is sent.
 */

const EMAIL_RX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const URL_RX = /^https?:\/\/[^\s"'<>]+$/i;
const SITE_RX = /^[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}([/?#][^\s"'<>]*)?$/i;
export const MAX_LOGOS = 6;

/** Where a logo goes when clicked: "brandx.com" is https://brandx.com. Null when it is not a web address. */
export const logoLink = (value) => {
  const v = String(value || '').trim();
  if (URL_RX.test(v)) return v;
  return SITE_RX.test(v) ? `https://${v}` : null;
};

/** A sender's logos, [{ src, href }]; one saved with the single logo_url reads as a list of one. */
export const logosOf = (sender) => (Array.isArray(sender?.logos) ? sender.logos
  : sender?.logo_url ? [{ src: sender.logo_url, href: '' }] : []);

const CONTACTS = {
  teams: (v) => (EMAIL_RX.test(v)
    ? { text: v, href: `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(v)}` }
    : /^https:\/\/([a-z0-9-]+\.)*(teams\.microsoft\.com|teams\.live\.com)\//i.test(v) && URL_RX.test(v) ? { text: 'Chat on Teams', href: v } : null),
  telegram: (v) => {
    const h = v.replace(/^(https?:\/\/)?(www\.)?(t\.me|telegram\.me)\//i, '').replace(/^@/, '').replace(/\/+$/, '');
    if (/^[A-Za-z][A-Za-z0-9_]{3,31}$/.test(h)) return { text: `@${h}`, href: `https://t.me/${h}` };
    const phone = v.replace(/[\s()-]/g, '');
    return /^\+\d{7,15}$/.test(phone) ? { text: v, href: `https://t.me/${phone}` } : null;
  },
  linkedin: (v) => {
    const path = v.replace(/^(https?:\/\/)?([a-z]{2,3}\.)?(www\.)?linkedin\.com\//i, '').replace(/^\/+|\/+$/g, '').split(/[?#]/)[0];
    if (!/^(in|company|pub)\/[^\s/"'<>]+$/i.test(path)) return null;
    return { text: `linkedin.com/${path}`, href: `https://www.linkedin.com/${path}` };
  }
};

export const CONTACT_FIELDS = [
  { key: 'teams', label: 'Teams', placeholder: 'sam@brandx.com', help: 'An email address or a Teams chat link.' },
  { key: 'telegram', label: 'Telegram', placeholder: '@sam_lee', help: 'A username like @sam_lee, a t.me link or a phone number with its country code.' },
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'linkedin.com/in/sam-lee', help: 'A profile link like linkedin.com/in/sam-lee.' }
];

/** Why a typed value cannot be used, or null. */
export const contactIssue = (key, value) => {
  const v = String(value || '').trim();
  if (!v) return null;
  if (key === 'logo') return URL_RX.test(v) ? null : 'Upload an image, or paste a link to one (https://...).';
  if (key === 'logo_link') return logoLink(v) ? null : 'The link a logo opens must be a web address, like brandx.com or https://brandx.com/partners.';
  return CONTACTS[key] && !CONTACTS[key](v) ? CONTACT_FIELDS.find((f) => f.key === key).help : null;
};

/** Why a list of logos cannot be saved, or null. A row left empty is fine: it is dropped. */
export const logosIssue = (logos) => {
  const list = (logos || []).filter((l) => String(l.src || '').trim() || String(l.href || '').trim());
  if (list.length > MAX_LOGOS) return `At most ${MAX_LOGOS} footer logos.`;
  for (const l of list) {
    if (!String(l.src || '').trim()) return 'A footer logo has a link but no image: upload one or paste its image link.';
    const issue = contactIssue('logo', l.src) || contactIssue('logo_link', l.href);
    if (issue) return issue;
  }
  return null;
};

/** The logos as saved: empty rows dropped, each link written in full. */
export const cleanLogos = (logos) => (logos || [])
  .filter((l) => String(l.src || '').trim())
  .map((l) => ({ src: l.src.trim(), href: logoLink(l.href) || '' }));

/** The sender's links, in signature order: [{ label, text, href }]. */
export const signatureLinks = (sender) => CONTACT_FIELDS.map((f) => {
  const v = String(sender?.[f.key] || '').trim();
  const link = v && CONTACTS[f.key](v);
  return link ? { label: f.label, ...link } : null;
}).filter(Boolean);

/**
 * "Telegram @sam_lee, linkedin.com/in/sam, sam@brandx.com" typed in one go:
 * each part to its field, named or recognised. { found, bad }: bad are the parts it could not read.
 */
export const parseContacts = (text) => {
  const found = {};
  const bad = [];
  for (const raw of String(text).split(/[,;\n]+/)) {
    const m = raw.trim().match(/^(teams|telegram|tg|linkedin)\b\s*[:=-]?\s*(.*)$/i);
    const named = m ? (m[1].toLowerCase() === 'tg' ? 'telegram' : m[1].toLowerCase()) : null;
    const part = (m ? m[2] : raw).trim();
    if (!part) continue;
    const key = named || (/linkedin\.com|^in\//i.test(part) ? 'linkedin'
      : /teams\.(microsoft|live)\.com/i.test(part) || EMAIL_RX.test(part) ? 'teams'
        : /^@|t\.me\/|^\+\d/.test(part) ? 'telegram' : null);
    if (key && CONTACTS[key](part)) found[key] = part;
    else bad.push(part);
  }
  return { found, bad };
};

/** Upload a logo; resolves to its public link, on the address this page reaches the server at. */
export const uploadLogo = async (file) => {
  const body = new FormData();
  body.append('logo', file);
  const res = await fetch(`${BASE}/signature-logo`, { method: 'POST', credentials: 'include', body });
  let data;
  try { data = await res.json(); } catch { data = { success: false, message: `The server answered ${res.status}.` }; }
  if (!data.success) throw new Error(data.message || 'The logo could not be uploaded.');
  return new URL(data.path, BASE).href;
};

/** The signature as the email shows it: lines, then the links, then the logos in a row. */
export const SignaturePreview = ({ lines, sender }) => {
  const links = signatureLinks(sender);
  const logos = cleanLogos(logosOf(sender)).filter((l) => URL_RX.test(l.src)).slice(0, MAX_LOGOS);
  return (
    <div className="rounded-lg bg-gray-50 p-3 text-sm text-gray-800 dark:bg-slate-950 dark:text-slate-200">
      <p className="mb-3 text-gray-500 dark:text-slate-400">...the email body.</p>
      {lines.map((l, i) => <div key={i} className={`min-h-[1.25rem]${i ? ' font-bold text-gray-900 dark:text-white' : ''}`}>{l}</div>)}
      {links.map((l) => (
        <div key={l.label}>{l.label}: <a href={l.href} target="_blank" rel="noreferrer" className="text-blue-700 underline dark:text-blue-300">{l.text}</a></div>
      ))}
      {logos.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          {logos.map((l, i) => {
            const img = <img src={l.src} alt="Footer logo" className="block h-auto max-h-[60px] w-auto max-w-[160px]" />;
            return l.href
              ? <a key={i} href={l.href} target="_blank" rel="noreferrer" title={`Opens ${l.href}`}>{img}</a>
              : <span key={i}>{img}</span>;
          })}
        </div>
      )}
    </div>
  );
};

const smallButton = 'inline-flex flex-none items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800';

/**
 * Footer logos, as many as MAX_LOGOS: each an image (uploaded, or its link
 * pasted) and, optionally, the page it opens when clicked.
 */
export const LogoEditor = ({ logos, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileRef = useRef(null);
  const list = logos || [];
  const full = list.length >= MAX_LOGOS;
  const setRow = (i, key, value) => onChange(list.map((l, j) => (j === i ? { ...l, [key]: value } : l)));

  const pick = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const src = await uploadLogo(file);
      onChange([...list, { src, href: '' }]);
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="md:col-span-2">
      <span className="mb-1 block text-xs font-medium text-gray-700 dark:text-slate-300">Footer logos (optional)</span>
      <div className="space-y-2">
        {list.map((l, i) => {
          const imageOk = String(l.src || '').trim() && !contactIssue('logo', l.src);
          const issue = contactIssue('logo', l.src) || contactIssue('logo_link', l.href);
          return (
            <div key={i}>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex h-9 w-14 flex-none items-center justify-center rounded border border-gray-200 bg-white p-0.5 dark:border-slate-700">
                  {imageOk
                    ? <img src={l.src.trim()} alt={`Logo ${i + 1}`} className="max-h-full max-w-full object-contain" />
                    : <ImagePlus className="h-4 w-4 text-gray-300 dark:text-slate-600" />}
                </div>
                <input className={`${inputCls} min-w-0 flex-1 basis-48`} value={l.src || ''} placeholder="Image link (https://...)"
                  aria-label={`Logo ${i + 1} image`} onChange={(e) => setRow(i, 'src', e.target.value)} />
                <div className="relative min-w-0 flex-1 basis-48">
                  <Link2 className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input className={`${inputCls} pl-8`} value={l.href || ''} placeholder="Opens when clicked, e.g. brandx.com (optional)"
                    aria-label={`Logo ${i + 1} link`} onChange={(e) => setRow(i, 'href', e.target.value)} />
                </div>
                <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} title="Remove this logo" className="flex-none text-gray-400 hover:text-red-600">
                  <X className="h-4 w-4" />
                </button>
              </div>
              {issue && <span className="mt-1 block text-xs text-red-600 dark:text-red-400">{issue}</span>}
            </div>
          );
        })}
      </div>
      <div className={`${list.length ? 'mt-2 ' : ''}flex flex-wrap items-center gap-2`}>
        <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif" className="hidden" aria-label="Upload a footer logo"
          onChange={(e) => pick(e.target.files?.[0])} />
        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading || full} title="Upload a PNG, JPG or GIF, under 1 MB" className={smallButton}>
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}Upload a logo
        </button>
        <button type="button" onClick={() => onChange([...list, { src: '', href: '' }])} disabled={full} className={smallButton}>
          <Link2 className="h-4 w-4" />Add by image link
        </button>
        <span className="text-xs text-gray-500 dark:text-slate-400">
          {full ? `That is the most: ${MAX_LOGOS} logos.` : `PNG, JPG or GIF under 1 MB, up to ${MAX_LOGOS}. Each can open a link when clicked.`}
        </span>
      </div>
      {uploadError && <span className="mt-1 block text-xs text-red-600 dark:text-red-400">{uploadError}</span>}
    </div>
  );
};

/** Teams, Telegram, LinkedIn and the footer logos for one sender. All optional. */
export const SignatureFields = ({ sender, onChange }) => (
  <>
    {CONTACT_FIELDS.map((f) => {
      const issue = contactIssue(f.key, sender[f.key]);
      return (
        <Field key={f.key} label={`${f.label} (optional)`}>
          <input className={inputCls} value={sender[f.key] || ''} placeholder={f.placeholder} onChange={(e) => onChange(f.key, e.target.value)} />
          {issue && <span className="mt-1 block text-xs text-red-600 dark:text-red-400">{issue}</span>}
        </Field>
      );
    })}
    <LogoEditor logos={logosOf(sender)} onChange={(logos) => onChange('logos', logos)} />
  </>
);
